/**
 * ScheduleOperationsContext.js
 * 
 * Business logic and side effects for the schedule system.
 * This context handles all operations, Firebase calls, calculations, and subscriptions.
 * 
 * Dependencies:
 * - ScheduleStateContext (for state and dispatch)
 * - AuthContext (for user and company info)
 * - ScheduleService (for Firebase operations)
 * - TimeSlotUtils (for time calculations)
 * - ScheduleValidationUtils (for validation)
 * 
 * @module ScheduleOperationsContext
 */

import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { useScheduleState, actionTypes } from './ScheduleStateContext';
import ScheduleService from '../../services/ScheduleService';
import { SchedulePeriod, ScheduleUtils } from '../../models/ScheduleModels';
import {
    TIME_SLOT_MINUTES,
    parseTimeString,
    calculateSelectedSlots as calculateSlotsUtil,
    generateSlotId,
    parseSlotId
} from '../../utils/TimeSlotUtils';
import {
    validateShift,
    checkShiftOverlap
} from '../../utils/ScheduleValidationUtils';

// ==================== LOCAL DATE HELPER ====================

/**
 * Formats a Date to YYYY-MM-DD using LOCAL time.
 * Never use toISOString() for date strings - it returns UTC and causes
 * a one-day offset for timezones ahead of UTC (e.g. Germany CEST = UTC+2).
 */
const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// ==================== CONTEXT CREATION ====================

const ScheduleOperationsContext = createContext(undefined);

// ==================== PROVIDER COMPONENT ====================

export const ScheduleOperationsProvider = ({ children }) => {
    const { state, dispatch } = useScheduleState();
    const { currentUser, currentCompany } = useAuth();

    // Derive companyId from currentCompany
    const companyId = currentCompany?.id;

    // ==================== INITIALIZATION ====================

    useEffect(() => {
        if (currentUser && currentCompany) {
            initializePermissions();
            loadHolidays();
            loadTemplates();
        }
        
        return () => {
            clearAllSubscriptions();
        };
    }, [currentUser, currentCompany]);

    // Update date range when view mode or current date changes
    useEffect(() => {
        updateDateRange();
    }, [state.viewMode, state.currentDate]);

    // Load schedules when date range or selected staff changes
    useEffect(() => {
        if (state.dateRange.start && state.dateRange.end) {
            if (state.selectedStaff) {
                loadScheduleForStaff(state.selectedStaff);
            } else if (state.selectedStaffList.length > 0) {
                loadSchedulesForStaffList(state.selectedStaffList);
            }
        }
    }, [state.dateRange, state.selectedStaff, state.selectedStaffList]);

    // Update real-time calculations when schedules or holidays change
    useEffect(() => {
        updateRealTimeCalculations();
    }, [state.schedules, state.holidays, state.dateRange]);

    // ==================== PERMISSION MANAGEMENT ====================

    const initializePermissions = async () => {
        if (!currentUser || !currentCompany) return;

        try {
            // Web app user is always admin with full permissions
            const canManageAllStaff = true;
            const userDepartmentIds = []; // Admin can access all departments

            const result = await ScheduleService.getManageableStaffIds(
                companyId, 
                'admin', 
                userDepartmentIds
            );

            if (result.success) {
                dispatch({
                    type: actionTypes.SET_PERMISSIONS,
                    payload: {
                        canManageAllStaff,
                        manageableStaffIds: result.staffIds,
                        userDepartmentIds
                    }
                });
                console.log('Permissions initialized:', {
                    canManageAllStaff,
                    manageableStaffCount: result.staffIds.length
                });
            } else {
                console.error('Failed to initialize permissions:', result.error);
            }
        } catch (error) {
            console.error('Error initializing permissions:', error);
        }
    };

    // ==================== DATA LOADING ====================

    const updateDateRange = () => {
        let dateRange;
        
        if (state.viewMode === 'week') {
            dateRange = ScheduleUtils.getWeekBoundaries(state.currentDate);
        } else {
            dateRange = ScheduleUtils.getMonthBoundaries(state.currentDate);
        }

        dispatch({
            type: actionTypes.SET_DATE_RANGE,
            payload: dateRange
        });
    };

    const loadHolidays = async () => {
        if (!companyId) return;

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'holidays', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'holidays' });

        try {
            // Load holidays for current year
            const year = new Date().getFullYear();
            const startDate = `${year}-01-01`;
            const endDate = `${year}-12-31`;

            const result = await ScheduleService.getHolidays(companyId, startDate, endDate);
            
            if (result.success) {
                dispatch({ type: actionTypes.SET_HOLIDAYS, payload: result.holidays });
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'holidays', error: error.message }
            });
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'holidays', value: false } });
        }
    };

    const loadTemplates = async () => {
        if (!companyId) return;

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'templates', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'templates' });

        try {
            const result = await ScheduleService.getScheduleTemplates(companyId);
            
            if (result.success) {
                dispatch({ type: actionTypes.SET_TEMPLATES, payload: result.templates });
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'templates', error: error.message }
            });
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'templates', value: false } });
        }
    };

    /**
     * Load schedule for a single staff member
     * Loads current week + adjacent weeks (previous and next) for smooth navigation
     * Uses real-time Firestore subscriptions
     */
    const loadScheduleForStaff = async (staffId) => {
        if (!companyId || !state.dateRange.start || !state.dateRange.end) return;

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'schedules', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'schedules' });

        try {
            // Clear existing subscription for this staff
            if (state.subscriptions[staffId]) {
                state.subscriptions[staffId]();
                dispatch({ type: actionTypes.REMOVE_SUBSCRIPTION, payload: staffId });
            }

            let unsubscribe;

            if (state.viewMode === 'month') {
                // ==================== MONTHLY VIEW ====================
                // Load entire month's data
                console.log('Loading schedules for month:', {
                    start: state.dateRange.start,
                    end: state.dateRange.end
                });

                // Get all weeks in the current month
                const monthStart = new Date(state.dateRange.start);
                const monthEnd   = new Date(state.dateRange.end);
                const weekStarts = [];

                // Start from first Monday of/before month start
                const currentDate  = new Date(monthStart);
                const dayOfWeek    = currentDate.getDay();
                const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                currentDate.setDate(currentDate.getDate() + mondayOffset);

                // Collect all week starts until we pass month end
                while (currentDate <= monthEnd) {
                    weekStarts.push(toLocalDateString(currentDate)); // FIXED
                    currentDate.setDate(currentDate.getDate() + 7);
                }

                console.log('Loading schedules for month weeks:', weekStarts);

                unsubscribe = ScheduleService.subscribeToStaffScheduleMultiWeek(
                    companyId,
                    staffId,
                    weekStarts,
                    (result) => {
                        if (result.success) {
                            dispatch({
                                type: actionTypes.UPDATE_SCHEDULE_FOR_STAFF,
                                payload: {
                                    staffId,
                                    shifts: result.shifts
                                }
                            });
                            updateSchedulePeriod(staffId, result.shifts);
                        } else {
                            dispatch({
                                type: actionTypes.SET_ERROR,
                                payload: { key: 'schedules', error: result.error }
                            });
                        }
                    }
                );
            } else {
                // ==================== WEEKLY VIEW ====================
                // Load 3 weeks (previous, current, next) for smooth navigation
                const currentWeekStart     = ScheduleUtils.getWeekBoundaries(state.currentDate).start;
                const currentWeekStartDate = new Date(currentWeekStart);

                const previousWeekStart = new Date(currentWeekStartDate);
                previousWeekStart.setDate(previousWeekStart.getDate() - 7);

                const nextWeekStart = new Date(currentWeekStartDate);
                nextWeekStart.setDate(nextWeekStart.getDate() + 7);

                const weekStarts = [
                    toLocalDateString(previousWeekStart), // FIXED
                    currentWeekStart,
                    toLocalDateString(nextWeekStart)       // FIXED
                ];

                console.log('Loading schedules for weeks:', weekStarts);

                unsubscribe = ScheduleService.subscribeToStaffScheduleMultiWeek(
                    companyId,
                    staffId,
                    weekStarts,
                    (result) => {
                        if (result.success) {
                            dispatch({
                                type: actionTypes.UPDATE_SCHEDULE_FOR_STAFF,
                                payload: {
                                    staffId,
                                    shifts: result.shifts
                                }
                            });
                            updateSchedulePeriod(staffId, result.shifts);
                        } else {
                            dispatch({
                                type: actionTypes.SET_ERROR,
                                payload: { key: 'schedules', error: result.error }
                            });
                        }
                    }
                );
            }

            if (unsubscribe) {
                dispatch({
                    type: actionTypes.ADD_SUBSCRIPTION,
                    payload: { key: staffId, unsubscribe }
                });
            }
        } catch (error) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'schedules', error: error.message }
            });
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'schedules', value: false } });
        }
    };

    const loadSchedulesForStaffList = async (staffIds) => {
        if (!companyId || !state.dateRange.start || !state.dateRange.end) return;

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'schedules', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'schedules' });

        try {
            // Clear existing subscriptions
            Object.keys(state.subscriptions).forEach(key => {
                if (state.subscriptions[key]) {
                    state.subscriptions[key]();
                }
            });
            dispatch({ type: actionTypes.CLEAR_SUBSCRIPTIONS });

            // Set up real-time listeners for all staff
            const unsubscribeAll = ScheduleService.subscribeToMultipleStaffSchedules(
                companyId,
                staffIds,
                state.dateRange.start,
                state.dateRange.end,
                (result) => {
                    if (result.success) {
                        dispatch({
                            type: actionTypes.UPDATE_SCHEDULE_FOR_STAFF,
                            payload: {
                                staffId: result.staffId,
                                shifts: result.shifts
                            }
                        });
                        updateSchedulePeriod(result.staffId, result.shifts);
                    }
                }
            );

            dispatch({
                type: actionTypes.ADD_SUBSCRIPTION,
                payload: { key: 'multiple_staff', unsubscribe: unsubscribeAll }
            });
        } catch (error) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'schedules', error: error.message }
            });
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'schedules', value: false } });
        }
    };

    // ==================== TIME SLOT SELECTION MANAGEMENT ====================

    const startTimeSlotSelection = (staffId, date, slotId) => {
        // Clear any previous selection
        clearTimeSlotSelection();
        
        dispatch({
            type: actionTypes.START_TIME_SLOT_SELECTION,
            payload: { staffId, date, slotId }
        });
    };

    const updateTimeSlotSelection = useCallback((endSlotId) => {
        if (!state.timeSlotSelection.dragStartSlotId || !endSlotId) return;

        const selectedSlots = calculateSelectedSlots(
            state.timeSlotSelection.dragStartSlotId,
            endSlotId,
            state.timeSlotSelection.staffId
        );

        const totalMinutes = selectedSlots.length * TIME_SLOT_MINUTES;
        
        dispatch({
            type: actionTypes.UPDATE_TIME_SLOT_SELECTION,
            payload: {
                endSlot: endSlotId,
                selectedSlots,
                totalMinutes
            }
        });

        // Update real-time calculations
        dispatch({
            type: actionTypes.UPDATE_REAL_TIME_CALCULATIONS,
            payload: {
                selectedDuration: {
                    hours: Math.floor(totalMinutes / 60),
                    minutes: totalMinutes % 60
                }
            }
        });
    }, [state.timeSlotSelection.dragStartSlotId, state.timeSlotSelection.staffId]);

    const endTimeSlotSelection = () => {
        dispatch({ type: actionTypes.END_TIME_SLOT_SELECTION });
        
        // Hide selection after a delay unless creating a shift
        setTimeout(() => {
            if (!state.timeSlotSelection.isSelecting) {
                dispatch({ type: actionTypes.SET_SELECTION_VISIBLE, payload: false });
            }
        }, 3000);
    };

    const clearTimeSlotSelection = () => {
        dispatch({ type: actionTypes.CLEAR_TIME_SLOT_SELECTION });
    };

    const setDragState = (isDragging) => {
        dispatch({ type: actionTypes.SET_DRAG_STATE, payload: isDragging });
    };

    const updateHoverSlot = (slotId) => {
        dispatch({ type: actionTypes.UPDATE_HOVER_SLOT, payload: slotId });
        
        // Update selection while hovering during drag
        if (state.timeSlotSelection.isDragging && slotId) {
            updateTimeSlotSelection(slotId);
        }
    };

    const calculateSelectedSlots = useCallback((startSlotId, endSlotId, staffId) => {
        return calculateSlotsUtil(startSlotId, endSlotId, staffId);
    }, []);

    // Create shift from selected time slots
    const createShiftFromSelection = async () => {
        if (!state.timeSlotSelection.selectedSlots.length || !state.timeSlotSelection.staffId || !state.timeSlotSelection.date) {
            return { success: false, error: 'No valid selection to create shift from' };
        }

        const selectedSlots = state.timeSlotSelection.selectedSlots.sort();
        const firstSlot = parseSlotId(selectedSlots[0]);
        const lastSlot  = parseSlotId(selectedSlots[selectedSlots.length - 1]);

        if (!firstSlot || !lastSlot) {
            return { success: false, error: 'Invalid slot selection' };
        }

        // Calculate start and end times
        const startTime       = new Date(`${firstSlot.date}T${firstSlot.time}:00`);
        const lastSlotMinutes = parseTimeString(lastSlot.time) + TIME_SLOT_MINUTES;
        const endHours        = Math.floor(lastSlotMinutes / 60);
        const endMins         = lastSlotMinutes % 60;
        const endTime         = new Date(`${lastSlot.date}T${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}:00`);

        const shiftData = {
            staffId:   state.timeSlotSelection.staffId,
            date:      state.timeSlotSelection.date,
            startTime: startTime.toISOString(),
            endTime:   endTime.toISOString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        const result = await createShift(shiftData);
        
        if (result.success) {
            clearTimeSlotSelection();
        }
        
        return result;
    };

    // ==================== SCHEDULE PERIOD CALCULATIONS ====================

    const updateSchedulePeriod = (staffId, shifts) => {
        const period = new SchedulePeriod({
            staffId,
            startDate: state.dateRange.start,
            endDate:   state.dateRange.end,
            shifts,
            holidays:  state.holidays
        });

        dispatch({
            type: actionTypes.UPDATE_SCHEDULE_PERIOD,
            payload: { staffId, period }
        });

        // Update weekly and monthly hours
        const totalHours    = period.getTotalScheduledHours();
        const adjustedHours = period.getAdjustedHours();

        if (state.viewMode === 'week') {
            dispatch({
                type: actionTypes.UPDATE_WEEKLY_HOURS,
                payload: { staffId, hours: totalHours }
            });
        } else {
            dispatch({
                type: actionTypes.UPDATE_MONTHLY_HOURS,
                payload: { staffId, hours: totalHours }
            });
        }

        // Update adjusted hours separately
        dispatch({
            type: actionTypes.UPDATE_ADJUSTED_HOURS,
            payload: { 
                staffId, 
                weeklyAdjusted:  adjustedHours,
                monthlyAdjusted: adjustedHours
            }
        });
    };

    const updateRealTimeCalculations = useCallback(() => {
        let weeklyTotal    = { planned: 0, adjusted: 0 };
        let monthlyTotal   = { planned: 0, adjusted: 0 };
        let conflictWarnings = [];

        // Calculate totals for selected staff
        state.selectedStaffList.forEach(staffId => {
            const weeklyHours     = state.weeklyHours[staffId]         || 0;
            const monthlyHours    = state.monthlyHours[staffId]        || 0;
            const adjustedWeekly  = state.adjustedWeeklyHours[staffId] || 0;
            const adjustedMonthly = state.adjustedMonthlyHours[staffId]|| 0;

            weeklyTotal.planned  += weeklyHours;
            weeklyTotal.adjusted += adjustedWeekly;
            monthlyTotal.planned += monthlyHours;
            monthlyTotal.adjusted+= adjustedMonthly;

            // Check for conflicts
            const shifts = state.schedules[staffId] || [];
            const period = new SchedulePeriod({
                staffId,
                startDate: state.dateRange.start,
                endDate:   state.dateRange.end,
                shifts,
                holidays:  state.holidays
            });

            const conflicts = period.getShiftConflicts();
            if (conflicts.length > 0) {
                conflictWarnings.push({
                    staffId,
                    conflicts: conflicts.length
                });
            }
        });

        dispatch({
            type: actionTypes.UPDATE_REAL_TIME_CALCULATIONS,
            payload: {
                weeklyTotal,
                monthlyTotal,
                conflictWarnings
            }
        });
    }, [state.selectedStaffList, state.weeklyHours, state.monthlyHours, state.adjustedWeeklyHours, state.adjustedMonthlyHours, state.schedules, state.holidays, state.dateRange]);

    // ==================== CRUD OPERATIONS ====================

    const createShift = async (shiftData) => {
        if (!companyId) return { success: false, error: 'Company ID not found' };

        // Validate shift data
        const validation = validateShift(shiftData);
        if (!validation.isValid) {
            return { success: false, error: validation.errors.join(', ') };
        }

        // Check for overlaps
        const existingShifts = state.schedules[shiftData.staffId] || [];
        const overlapCheck   = checkShiftOverlap(shiftData, existingShifts);
        if (overlapCheck.hasOverlap) {
            return { 
                success: false, 
                error: `Shift overlaps with existing shift(s): ${overlapCheck.overlappingShifts.map(s => 
                    `${s.startTime?.toDate ? s.startTime.toDate().toLocaleTimeString() : new Date(s.startTime).toLocaleTimeString()}-${s.endTime?.toDate ? s.endTime.toDate().toLocaleTimeString() : new Date(s.endTime).toLocaleTimeString()}`
                ).join(', ')}`
            };
        }

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'operations' });

        // Add optimistic update
        const optimisticId    = `temp_${Date.now()}`;
        const optimisticShift = {
            ...shiftData,
            id:        optimisticId,
            createdAt: new Date(),
            updatedAt: new Date()
        };

        dispatch({
            type: actionTypes.ADD_OPTIMISTIC_UPDATE,
            payload: { id: optimisticId, data: optimisticShift }
        });

        dispatch({
            type: actionTypes.ADD_SHIFT,
            payload: { staffId: shiftData.staffId, shift: optimisticShift }
        });

        try {
            const result = await ScheduleService.createShift(companyId, shiftData);
            
            // Remove optimistic update
            dispatch({ type: actionTypes.REMOVE_OPTIMISTIC_UPDATE, payload: optimisticId });
            
            if (result.success) {
                // Real-time listener will handle the actual update
                return result;
            } else {
                // Revert optimistic update on error
                dispatch({
                    type: actionTypes.REMOVE_SHIFT,
                    payload: { staffId: shiftData.staffId, shiftId: optimisticId }
                });
                throw new Error(result.error);
            }
        } catch (error) {
            // Revert optimistic update on error
            dispatch({ type: actionTypes.REMOVE_OPTIMISTIC_UPDATE, payload: optimisticId });
            dispatch({
                type: actionTypes.REMOVE_SHIFT,
                payload: { staffId: shiftData.staffId, shiftId: optimisticId }
            });

            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'operations', error: error.message }
            });
            return { success: false, error: error.message };
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: false } });
        }
    };

    const updateShift = async (staffId, shiftId, updates) => {
        if (!companyId) return { success: false, error: 'Company ID not found' };

        // Find the original shift
        let originalShift  = null;
        const staffShifts  = state.schedules[staffId] || [];
        originalShift      = staffShifts.find(s => s.id === shiftId);

        if (!originalShift) {
            return { success: false, error: 'Shift not found' };
        }

        // Create updated shift for validation
        const updatedShift = {
            ...originalShift,
            ...updates,
            id: shiftId,
            staffId
        };

        // Validate shift data
        const validation = validateShift(updatedShift);
        if (!validation.isValid) {
            return { success: false, error: validation.errors.join(', ') };
        }

        // Check for overlaps (excluding current shift)
        const existingShifts = state.schedules[staffId] || [];
        const overlapCheck   = checkShiftOverlap(updatedShift, existingShifts, shiftId);
        if (overlapCheck.hasOverlap) {
            return { 
                success: false, 
                error: `Shift overlaps with existing shift(s): ${overlapCheck.overlappingShifts.map(s => 
                    `${s.startTime?.toDate ? s.startTime.toDate().toLocaleTimeString() : new Date(s.startTime).toLocaleTimeString()}-${s.endTime?.toDate ? s.endTime.toDate().toLocaleTimeString() : new Date(s.endTime).toLocaleTimeString()}`
                ).join(', ')}`
            };
        }

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'operations' });

        // Add optimistic update
        dispatch({
            type: actionTypes.UPDATE_SHIFT,
            payload: { staffId, shiftId, shift: updatedShift }
        });

        try {
            const result = await ScheduleService.updateShift(companyId, staffId, shiftId, updates);
            
            if (result.success) {
                // Real-time listener will handle the actual update
                return result;
            } else {
                // Revert optimistic update on error
                dispatch({
                    type: actionTypes.UPDATE_SHIFT,
                    payload: { staffId, shiftId, shift: originalShift }
                });
                throw new Error(result.error);
            }
        } catch (error) {
            // Revert optimistic update on error
            dispatch({
                type: actionTypes.UPDATE_SHIFT,
                payload: { staffId, shiftId, shift: originalShift }
            });

            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'operations', error: error.message }
            });
            return { success: false, error: error.message };
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: false } });
        }
    };

    const deleteShift = async (staffId, shiftId) => {
        if (!companyId) return { success: false, error: 'Company ID not found' };

        // Find the shift to delete
        const staffShifts  = state.schedules[staffId] || [];
        const shiftToDelete = staffShifts.find(s => s.id === shiftId);

        if (!shiftToDelete) {
            return { success: false, error: 'Shift not found' };
        }

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'operations' });

        // Add optimistic update (remove shift immediately)
        dispatch({
            type: actionTypes.REMOVE_SHIFT,
            payload: { staffId, shiftId }
        });

        try {
            const result = await ScheduleService.deleteShift(companyId, staffId, shiftId);
            
            if (result.success) {
                // Real-time listener will handle the confirmation
                return result;
            } else {
                // Revert optimistic update on error
                dispatch({
                    type: actionTypes.ADD_SHIFT,
                    payload: { staffId, shift: shiftToDelete }
                });
                throw new Error(result.error);
            }
        } catch (error) {
            // Revert optimistic update on error
            dispatch({
                type: actionTypes.ADD_SHIFT,
                payload: { staffId, shift: shiftToDelete }
            });

            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'operations', error: error.message }
            });
            return { success: false, error: error.message };
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: false } });
        }
    };

    // ==================== BATCH DELETE OPERATIONS ====================

    /**
     * Delete all future shifts in a batch
     * Used when user wants to delete all repeating occurrences from a specific date onwards
     */
    const deleteFutureBatchShifts = async (staffId, batchId, fromDate) => {
        if (!companyId) return { success: false, error: 'Company ID not found' };

        if (!batchId) {
            return { success: false, error: 'Batch ID is required' };
        }

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'operations' });

        // Find all shifts in this batch from the specified date onwards
        const staffShifts    = state.schedules[staffId] || [];
        const shiftsToDelete = staffShifts.filter(
            shift => shift.batchId === batchId && shift.date >= fromDate
        );

        if (shiftsToDelete.length === 0) {
            return { success: false, error: 'No future shifts found in this batch' };
        }

        console.log(`Deleting ${shiftsToDelete.length} future batch shifts`, {
            staffId,
            batchId,
            fromDate,
            shifts: shiftsToDelete.map(s => s.date)
        });

        // Optimistic update - remove all shifts immediately
        shiftsToDelete.forEach(shift => {
            dispatch({
                type: actionTypes.REMOVE_SHIFT,
                payload: { staffId, shiftId: shift.id }
            });
        });

        try {
            const result = await ScheduleService.deleteFutureBatchShifts(
                companyId,
                staffId,
                batchId,
                fromDate
            );

            if (result.success || result.deletedCount >= 0) {
                console.log(`Successfully deleted ${result.deletedCount || result} batch shifts`);
                return {
                    success:      true,
                    deletedCount: result.deletedCount || result
                };
            } else {
                // Revert optimistic updates on error
                shiftsToDelete.forEach(shift => {
                    dispatch({
                        type: actionTypes.ADD_SHIFT,
                        payload: { staffId, shift }
                    });
                });
                throw new Error(result.error || 'Failed to delete batch shifts');
            }
        } catch (error) {
            console.error('Error deleting batch shifts:', error);
            
            // Revert optimistic updates on error
            shiftsToDelete.forEach(shift => {
                dispatch({
                    type: actionTypes.ADD_SHIFT,
                    payload: { staffId, shift }
                });
            });

            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'operations', error: error.message }
            });
            return { success: false, error: error.message };
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: false } });
        }
    };

    // ==================== WEEKLY SCHEDULE SAVE OPERATIONS ====================

    /**
     * Save all shifts for a specific week to Firestore
     */
    const saveWeeklySchedule = async (staffId, weekStartDate = null) => {
        if (!companyId) return { success: false, error: 'Company ID not found' };
        
        // Use current week if not specified
        const targetWeekStart = weekStartDate || ScheduleUtils.getWeekBoundaries(state.currentDate).start;
        
        // Calculate week end date (Sunday) using local date formatting
        const weekStartDateObj = new Date(targetWeekStart);
        const weekEndDateObj   = new Date(weekStartDateObj);
        weekEndDateObj.setDate(weekEndDateObj.getDate() + 6);
        const weekEnd = toLocalDateString(weekEndDateObj); // FIXED
        
        // Get all shifts for this staff
        const allShifts = state.schedules[staffId] || [];
        
        // Filter shifts for this specific week only
        const weekShifts = allShifts.filter(shift => 
            shift.date >= targetWeekStart && shift.date <= weekEnd
        );
        
        console.log('Saving weekly schedule:', {
            staffId,
            weekStart: targetWeekStart,
            weekEnd,
            shiftCount: weekShifts.length
        });
        
        if (weekShifts.length === 0) {
            return { 
                success: false, 
                error: 'No shifts to save for this week. Please add shifts first.' 
            };
        }
        
        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'operations' });
        
        try {
            const result = await ScheduleService.saveWeeklySchedule(
                companyId,
                staffId,
                targetWeekStart,
                weekShifts
            );
            
            if (!result.success) {
                throw new Error(result.error);
            }
            
            console.log('Weekly schedule saved successfully:', result);
            return { 
                success: true, 
                message: `Schedule saved successfully for week of ${targetWeekStart}` 
            };
            
        } catch (error) {
            console.error('Error saving weekly schedule:', error);
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'operations', error: error.message }
            });
            return { success: false, error: error.message };
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: false } });
        }
    };

    /**
     * Save schedules for multiple staff members for a specific week
     */
    const saveMultipleWeeklySchedules = async (staffIds = null, weekStartDate = null) => {
        const targetStaffIds = staffIds || state.selectedStaffList;
        
        if (targetStaffIds.length === 0) {
            return { 
                success: false, 
                error: 'No staff members selected. Please select at least one staff member.' 
            };
        }
        
        console.log('Saving schedules for multiple staff:', targetStaffIds);
        
        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: true } });
        
        try {
            const results  = await Promise.all(
                targetStaffIds.map(staffId => saveWeeklySchedule(staffId, weekStartDate))
            );
            
            const failures  = results.filter(r => !r.success);
            const successes = results.filter(r => r.success);
            
            if (failures.length > 0) {
                console.error('Some schedules failed to save:', failures);
                return { 
                    success:       false, 
                    error:         `Failed to save ${failures.length} of ${targetStaffIds.length} schedules`,
                    details:       failures,
                    partialSuccess: successes.length > 0
                };
            }
            
            console.log('All schedules saved successfully');
            return { 
                success: true, 
                message: `Successfully saved schedules for ${targetStaffIds.length} staff members` 
            };
            
        } catch (error) {
            console.error('Error saving multiple schedules:', error);
            return { success: false, error: error.message };
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: false } });
        }
    };

    // ==================== BULK OPERATIONS ====================

    const createShiftsBatch = async (shifts) => {
        if (!companyId) return { success: false, error: 'Company ID not found' };

        // Validate all shifts
        for (const shift of shifts) {
            const validation = validateShift(shift);
            if (!validation.isValid) {
                return { success: false, error: `Invalid shift data: ${validation.errors.join(', ')}` };
            }
        }

        dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: true } });
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: 'operations' });

        try {
            const result = await ScheduleService.createShiftsBatch(companyId, shifts);
            
            if (!result.success) {
                throw new Error(result.error);
            }
            
            return result;
        } catch (error) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: { key: 'operations', error: error.message }
            });
            return { success: false, error: error.message };
        } finally {
            dispatch({ type: actionTypes.SET_LOADING, payload: { key: 'operations', value: false } });
        }
    };

    const applyTemplate = async (staffId, templateId, dates) => {
        if (!companyId) return { success: false, error: 'Company ID not found' };

        return await ScheduleService.applyTemplateToStaff(companyId, staffId, templateId, dates);
    };

    const copyWeekSchedule = async (staffId, sourceWeekStart, targetWeekStart) => {
        if (!companyId) return { success: false, error: 'Company ID not found' };

        return await ScheduleService.copyWeekSchedule(companyId, staffId, sourceWeekStart, targetWeekStart);
    };

    // ==================== SELECTION MANAGEMENT ====================

    const setSelectedStaff = (staffId) => {
        dispatch({ type: actionTypes.SET_SELECTED_STAFF, payload: staffId });
        dispatch({ type: actionTypes.SET_SELECTED_STAFF_LIST, payload: staffId ? [staffId] : [] });
    };

    const setSelectedStaffList = (staffIds) => {
        dispatch({ type: actionTypes.SET_SELECTED_STAFF_LIST, payload: staffIds });
        dispatch({ type: actionTypes.SET_SELECTED_STAFF, payload: staffIds.length === 1 ? staffIds[0] : null });
    };

    const addToStaffSelection = (staffId) => {
        dispatch({ type: actionTypes.ADD_TO_STAFF_SELECTION, payload: staffId });
    };

    const removeFromStaffSelection = (staffId) => {
        dispatch({ type: actionTypes.REMOVE_FROM_STAFF_SELECTION, payload: staffId });
    };

    // ==================== VIEW MANAGEMENT ====================

    const setCurrentDate = (date) => {
        dispatch({ type: actionTypes.SET_CURRENT_DATE, payload: date });
    };

    const setViewMode = (mode) => {
        dispatch({ type: actionTypes.SET_VIEW_MODE, payload: mode });
    };

    const navigateDate = (direction) => {
        const [year, month, day] = state.currentDate.split('-').map(Number);
        const currentDate = new Date(year, month - 1, day); // LOCAL midnight - no UTC ambiguity
        const offset      = state.viewMode === 'week' ? 7 : 30;
        const multiplier  = direction === 'next' ? 1 : -1;

        currentDate.setDate(currentDate.getDate() + (offset * multiplier));

        dispatch({
            type:    actionTypes.SET_CURRENT_DATE,
            payload: toLocalDateString(currentDate) // FIXED
        });
    };

    // ==================== UTILITY FUNCTIONS ====================

    const clearError = (key) => {
        dispatch({ type: actionTypes.CLEAR_ERROR, payload: key });
    };

    const clearAllSubscriptions = () => {
        Object.values(state.subscriptions).forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        });
        dispatch({ type: actionTypes.CLEAR_SUBSCRIPTIONS });
    };

    const getScheduleForStaff = (staffId) => {
        return state.schedules[staffId] || [];
    };

    const getSchedulePeriodForStaff = (staffId) => {
        return state.schedulePeriods[staffId] || null;
    };

    const getHoursForStaff = (staffId, adjusted = false) => {
        if (state.viewMode === 'week') {
            return adjusted ? (state.adjustedWeeklyHours[staffId] || 0) : (state.weeklyHours[staffId] || 0);
        } else {
            return adjusted ? (state.adjustedMonthlyHours[staffId] || 0) : (state.monthlyHours[staffId] || 0);
        }
    };

    const canManageStaff = (staffId) => {
        return state.canManageAllStaff || state.manageableStaffIds.includes(staffId);
    };

    const validateShiftData = (shiftData) => {
        return validateShift(shiftData);
    };

    const checkForShiftOverlap = (shiftData, excludeShiftId = null) => {
        const existingShifts = state.schedules[shiftData.staffId] || [];
        return checkShiftOverlap(shiftData, existingShifts, excludeShiftId);
    };

    // ==================== CONTEXT VALUE ====================

    const contextValue = {
        // Data loading
        loadScheduleForStaff,
        loadSchedulesForStaffList,
        loadHolidays,
        loadTemplates,
        
        // Time slot selection management
        startTimeSlotSelection,
        updateTimeSlotSelection,
        endTimeSlotSelection,
        clearTimeSlotSelection,
        setDragState,
        updateHoverSlot,
        calculateSelectedSlots,
        createShiftFromSelection,
        
        // CRUD operations
        createShift,
        updateShift,
        deleteShift,
        deleteFutureBatchShifts,
        
        // Weekly schedule save operations
        saveWeeklySchedule,
        saveMultipleWeeklySchedules,
        
        // Bulk operations
        createShiftsBatch,
        applyTemplate,
        copyWeekSchedule,
        
        // Selection management
        setSelectedStaff,
        setSelectedStaffList,
        addToStaffSelection,
        removeFromStaffSelection,
        
        // View management
        setCurrentDate,
        setViewMode,
        navigateDate,
        
        // Utility functions
        clearError,
        getScheduleForStaff,
        getSchedulePeriodForStaff,
        getHoursForStaff,
        canManageStaff,
        validateShiftData,
        checkForShiftOverlap
    };

    return (
        <ScheduleOperationsContext.Provider value={contextValue}>
            {children}
        </ScheduleOperationsContext.Provider>
    );
};

// ==================== CUSTOM HOOK ====================

export const useScheduleOperations = () => {
    const context = useContext(ScheduleOperationsContext);
    
    if (context === undefined) {
        throw new Error('useScheduleOperations must be used within a ScheduleOperationsProvider');
    }
    
    return context;
};

export default ScheduleOperationsContext;